/**
 * Creates a RegExp pattern to match Spark plan variable keys.
 * Escapes $ characters and optionally wraps pattern in a capture group.
 *
 * @param mapObj - Dictionary of Spark plan variables (e.g., {"$var1": "value1"})
 * @param options.isMatchGroup - If true, wraps pattern in capture group for matched extraction
 * @returns RegExp to match variable keys, or null if mapObj is empty/undefined
 *
 * @example
 * prepareSparkPlanVarsRegex({"$var1": "a", "$var2": "b"})
 * // Returns: /\$var1|\$var2/gi
 */
export const prepareSparkPlanVarsRegex = (
  mapObj: Record<string, string> | undefined,
  options?: {
    isMatchGroup?: boolean;
  }
): RegExp | null => {
  if (!mapObj) return null;

  const keys = Object.keys(mapObj);
  if (keys.length === 0) return null;

  const allVars = keys.map((key) => key.replaceAll("$", `\\$`)).join("|");

  if (options?.isMatchGroup) {
    return new RegExp(`(${allVars})`, "gi");
  }

  return new RegExp(allVars, "gi");
};

/**
 * Replaces all Spark plan variable keys in a string with their corresponding values.
 * Uses regex matching to find and replace variable placeholders.
 *
 * @param str - Source string containing variable placeholders
 * @param mapObj - Dictionary mapping variable keys to their values
 * @returns String with all variables replaced by their values
 *
 * @example
 * replaceAll("SELECT $col FROM $table", {"$col": "id", "$table": "users"})
 * // Returns: "SELECT id FROM users"
 */
export const replaceAll = (
  str: string,
  mapObj: Record<string, string> | undefined
): string => {
  if (!mapObj) return str;

  const re = prepareSparkPlanVarsRegex(mapObj);
  if (!re) return str;

  return str.replace(re, (matched) => mapObj[matched]);
};
